import React, { useMemo } from "react";
import CreatableSelect from "react-select/creatable";

// Free-text field with a type-to-filter dropdown of existing values: pick one
// that already exists, or type a new one and choose `+ Add "..."`. The value
// is a plain string (what gets saved), never an id. Matching is
// case-insensitive, so "toner" and "Toner" are offered once and typing an
// existing value in another case selects the existing spelling instead of
// adding a near-duplicate.
const clean = (v) => String(v ?? "").trim();

export default function CreatableTextSelect({
  options = [],
  value,
  onChange,
  placeholder = "Select or type...",
  inputId,
  isDisabled = false,
}) {
  const choices = useMemo(() => {
    const seen = new Map();
    options.forEach((raw) => {
      const text = clean(raw);
      if (text && !seen.has(text.toLowerCase())) seen.set(text.toLowerCase(), text);
    });
    return [...seen.values()]
      .sort((a, b) => a.localeCompare(b))
      .map((text) => ({ value: text, label: text }));
  }, [options]);

  const current = clean(value);
  const selected = current
    ? choices.find((o) => o.value.toLowerCase() === current.toLowerCase()) || { value: current, label: current }
    : null;

  return (
    <CreatableSelect
      inputId={inputId}
      aria-label={placeholder}
      classNamePrefix="member-picker"
      options={choices}
      value={selected}
      onChange={(opt) => onChange(opt ? clean(opt.value) : "")}
      placeholder={placeholder}
      isClearable
      isDisabled={isDisabled}
      isValidNewOption={(input) =>
        !!clean(input) && !choices.some((o) => o.value.toLowerCase() === clean(input).toLowerCase())
      }
      formatCreateLabel={(input) => `+ Add "${clean(input)}"`}
      createOptionPosition="first"
      noOptionsMessage={() => "Type to add a new value"}
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      // Same modal layering as components/SearchableSelect.jsx.
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 1400 }) }}
    />
  );
}
